/**
 * AlertsList Component
 * Displays dashboard alerts and notifications with color-coded severity
 *
 * @version 2025.1.0
 */
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Skeleton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Notifications,
  ArrowForward,
  Warning,
  Error,
  Info,
  CheckCircle,
  NotificationsNone,
} from '@mui/icons-material';
import { AlertsListProps, DashboardAlert, AlertType } from '../../../types/dashboard';

/**
 * Get icon based on alert type
 */
const getAlertIcon = (type: AlertType) => {
  switch (type) {
    case 'error':
      return <Error />;
    case 'warning':
      return <Warning />;
    case 'success':
      return <CheckCircle />;
    case 'info':
    default:
      return <Info />;
  }
};

/**
 * Get color based on alert type
 */
const getAlertColor = (type: AlertType): 'error' | 'warning' | 'success' | 'info' => {
  switch (type) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'success':
      return 'success';
    case 'info':
    default:
      return 'info';
  }
};

/**
 * Single alert item component
 */
const AlertItem: React.FC<{
  alert: DashboardAlert;
  onClick?: (alert: DashboardAlert) => void;
}> = ({ alert, onClick }) => {
  const theme = useTheme();
  const color = getAlertColor(alert.type);

  return (
    <ListItem
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 1,
        mb: 1,
        bgcolor: alpha(theme.palette[color].main, 0.08),
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick
          ? {
              bgcolor: alpha(theme.palette[color].main, 0.15),
            }
          : {},
        '&:last-child': {
          mb: 0,
        },
      }}
      onClick={() => onClick?.(alert)}
    >
      <ListItemIcon
        sx={{
          minWidth: 36,
          color: `${color}.main`,
        }}
      >
        {getAlertIcon(alert.type)}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={500}>
            {alert.message}
          </Typography>
        }
        sx={{ my: 0 }}
      />
    </ListItem>
  );
};

/**
 * Loading skeleton for alerts
 */
const LoadingSkeleton: React.FC = () => (
  <Box>
    {[1, 2, 3].map((i) => (
      <Box
        key={i}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          borderRadius: 1,
          mb: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1.5 }} />
        <Skeleton variant="text" width="80%" height={20} />
      </Box>
    ))}
  </Box>
);

/**
 * Empty state when no alerts
 */
const EmptyState: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4,
      color: 'text.secondary',
    }}
  >
    <NotificationsNone sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
    <Typography variant="body2">Nenhum alerta pendente</Typography>
    <Typography variant="caption" color="text.disabled">
      Tudo em ordem!
    </Typography>
  </Box>
);

const AlertsList: React.FC<AlertsListProps> = ({
  rotulo,
  alerts,
  loading = false,
  onViewAll,
  onAlertClick,
}) => {
  // Sort alerts by severity (error > warning > info > success)
  const sortedAlerts = [...alerts].sort((a, b) => {
    const priority: Record<AlertType, number> = {
      error: 0,
      warning: 1,
      info: 2,
      success: 3,
    };
    return priority[a.type] - priority[b.type];
  });

  const errorCount = alerts.filter((a) => a.type === 'error').length;
  const warningCount = alerts.filter((a) => a.type === 'warning').length;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications color="primary" />
            {rotulo}
          </Typography>
          {alerts.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {errorCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                    fontWeight: 600,
                  }}
                >
                  {errorCount}
                </Typography>
              )}
              {warningCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    fontWeight: 600,
                  }}
                >
                  {warningCount}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <LoadingSkeleton />
          ) : sortedAlerts.length === 0 ? (
            <EmptyState />
          ) : (
            <List disablePadding>
              {sortedAlerts.map((alert, index) => (
                <AlertItem
                  key={`${alert.module}-${index}`}
                  alert={alert}
                  onClick={onAlertClick}
                />
              ))}
            </List>
          )}
        </Box>

        {onViewAll && alerts.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              variant="text"
              color="primary"
              endIcon={<ArrowForward />}
              onClick={onViewAll}
              fullWidth
              sx={{ justifyContent: 'center' }}
            >
              Ver todos os alertas
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertsList;
